<!-- 'Home.vue' is the B360 Dashboard -->
<template>
	<main id="home" class="page dashboard-content">
		<div class="center-dashboard-area">
			<UnpublishedVideoBanner />
			<section v-if="hasAccessToClientConnect" class="client-connect">
				<ClientConnectDashboard />
			</section>
			<section class="suggested-collection">
				<header class="suggested-text">
					<h1>Today's Suggested Posts</h1>
					<p class="section-description">
						Take a look at today's suggested social posts available in the
						<router-link
							:to="'/marketing-calendar/suggested'"
							class="description-link"
							>marketing calendar.</router-link
						>
					</p>
				</header>
				<div class="suggested-post-list">
					<SuggestedPost v-if="suggestedPosts.length === 0" />
					<SuggestedPost
						v-for="(post, index) in suggestedPosts"
						class="suggested-post"
						:class="{ 'last-post': suggestedPosts.length - 1 === index }"
						:key="index"
						:image="post.previewUri"
						:title="post.title"
						:content="post.description"
						@click.native="handleSuggestedPostClick(post)"
					/>
				</div>
				<router-link
					tag="button"
					to="/marketing-calendar/suggested"
					class="b360-btn b360-btn--secondary view-all"
					aria-label="View all suggested posts"
					>View All
				</router-link>
			</section>
		</div>
		<section class="side-column">
			<DashboardHighlightColumn />
		</section>
		<SocialComposer
			:image="image"
			:content="content"
			:mediaData="mediaData"
			:mediaTypeId="mediaTypeId"
			:campaignId="campaignId"
			ref="socialComposer"
			@socialComposerClose="handleSocialComposerClose"
		/>
		<div
			v-if="
				browser.includes('iPhone OS 13_2') || browser.includes('iPhone OS 13_1')
			"
		>
			<modal
				v-if="
					browser.includes('iPhone OS 13_2') ||
					browser.includes('iPhone OS 13_1')
				"
				modalTitle="Attention"
				@clicked="iOSModal = false"
				class="disable-cancel ios-modal"
				v-show="iOSModal"
				:actionBtnName="'Close'"
			>
				<div slot="modalContent">
					<p>
						Some features of Brand360 will not work properly until you upgrade
						your mobile device to the latest version of iOS. For instructions on
						how to upgrade,
						<a
							class="apple-hyperlink"
							href="https://support.apple.com/en-us/HT204204"
							target="_blank"
							>click here.</a
						>
					</p>
				</div>
			</modal>
		</div>
	</main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SuggestedPost from '@/components/marketing-calendar/SuggestedPost.vue';
import Modal from '@/components/global/Modal.vue';
import DashboardHighlightColumn from '@/components/cards/DashboardHighlightColumn.vue';
import SuggestedPostService from '@/services/toolbox/suggested-post-service';
import ClientConnectDashboard from './client-connect-v2/ClientConnectDashboard.vue';
import SocialComposer from '@/components/global/SocialComposer.vue';
import UnpublishedVideoBanner from './client-connect-v2/ClientConnectDashboardVideoBanner.vue';

export default {
	name: 'HomeOrDashboard',
	components: {
		SuggestedPost,
		DashboardHighlightColumn,
		Modal,
		ClientConnectDashboard,
		SocialComposer,
		UnpublishedVideoBanner,
	},
	data() {
		return {
			iOSModal: true,
			browser: navigator.userAgent,
			timeModalClosed: new Date(),
			mediaData: [],
			suggestedPosts: [],
			image: '',
			content: '',
			mediaTypeId: null,
			campaignId: null,
		};
	},
	computed: {
		...mapGetters(['hasAccessToClientConnect']),
	},
	methods: {
		...mapActions([
			'dashboardCreated',
			'dashboardDestroyed',
			'brandBuilderDestroyed',
			'getUserHasVideoReviews',
		]),
		sendData({ previewUri, description, medias, campaignId }) {
			this.image = previewUri;
			this.content = description;
			this.mediaData = [previewUri];
			this.mediaTypeId = medias[0].mediaTypeId || 1;
			this.campaignId = campaignId;
		},
		clearData() {
			this.image = null;
			this.content = null;
			this.mediaData = [];
			this.mediaTypeId = null;
			this.campaignId = null;
		},
		handleSuggestedPostClick(post, postsDate) {
			const msBeforeCanOpenModal = 1500;
			// Calculate the milliseconds since the modal was last closed
			const msSinceClosedModal =
				new Date().getTime() - this.timeModalClosed.getTime();
			// Determine the remaining time before the modal can be opened again
			const remainingTime = msBeforeCanOpenModal - msSinceClosedModal;

			// Define a function to execute the modal-related operations
			const openModal = () => {
				this.sendData(post);
				this.$refs.socialComposer.validateShowModal();
				this.$refs.socialComposer.handleModalDateClick(postsDate);
			};

			/*
			 * If the remaining time is less than or equal to 0, execute the operations immediately.
			 * Otherwise, delay the execution until the remaining time has elapsed.
			 * This ensures the modal operations are not initiated too quickly in succession,
			 * allowing for any necessary preparations, such as loading external resources.
			 */
			if (remainingTime <= 0) {
				openModal();
			} else {
				setTimeout(openModal, remainingTime);
			}
		},
		handleSocialComposerClose() {
			this.timeModalClosed = new Date();
			this.clearData();
		},
	},
	async created() {
		await this.getUserHasVideoReviews();
		this.dashboardCreated();
		SuggestedPostService.getTodaysSuggestedPosts().then((response) => {
			this.suggestedPosts = response.data;
		});
	},
	destroyed() {
		this.dashboardDestroyed();
		if (this.$route.matched[0].path !== '/campaign-editor-workspace') {
			this.brandBuilderDestroyed();
		}
	},
};
</script>

<style lang="scss" scoped>
#home {
	margin-top: -$context-nav-height;
	overflow-x: hidden;
}

.composer-post-wrapper {
	position: relative;
}

.apple-hyperlink {
	color: $brand-blue;
	font-weight: bold;
	text-decoration: none;
}

.page {
	@include rem('grid-column-gap', 15px);

	@include xl {
		display: block;
	}

	@include sm {
		display: grid;
		grid-template-columns: 1fr;
	}
}

.center-dashboard-area {
	@include lg {
		display: block;
		float: left;
		width: 66%;
		@include rem('padding', 30px);
	}

	@include xl {
		width: 70%;
	}

	@include phablet {
		clear: both;
		display: grid;
		grid-template-columns: minmax(0, 1fr);
	}

	@include sm {
		@include rem('padding', 15px);
	}
}

.client-connect {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	@include rem(margin-bottom, 30px);
}

.suggested-collection {
	@include lg {
		grid-template-columns: minmax(0, 1fr);
	}
}

.suggested-collection {
	.suggested-post-list {
		@include mobile {
			grid-template-columns: minmax(0, 1fr);
		}
	}
}

.dashboard-content .suggested-post {
	display: grid;
	grid-column-start: 1;
	background-color: $white;

	@include mobile {
		grid-template-columns: minmax(0, 1fr);
	}
}

.suggested-text {
	display: grid;
	grid-template-columns: 87% 1fr;

	@include mobile {
		grid-template-columns: 68% 1fr;
	}

	@include larger-than-mobile {
		grid-template-columns: 75% 1fr;
	}

	@include tablet {
		grid-template-columns: 85% 1fr;
	}

	@include lg {
		grid-template-columns: 80% 1fr;
	}
}

.suggested-text .suggested-description {
	@include rem(margin-top, 5px);
}

.side-column {
	@include mobile {
		@include rem(margin, 15px);
		grid-template-columns: 1fr;
		grid-column-start: 1;
		grid-row-start: 2;
	}

	@include sm {
		display: block;
		@include rem(margin, 15px 15px);
	}

	@include lg {
		display: grid;
		@include rem(margin-top, 30px);
		@include rem(margin-right, 30px);
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto auto;
	}

	@include xl {
		display: grid;
		grid-template-columns: 1fr;
		@include rem(margin-right, 30px);
	}
}

.side-column {
	.dashboard-highlight-column {
		@include phablet {
			width: 100%;
			max-width: unset;
			justify-content: center;
			display: grid;
			grid-gap: 15px;
			grid-template-columns: minmax(0, 1fr);
			@include rem(margin, 15px 0);
		}

		@include tablet {
			max-width: 100%;
			grid-template-columns: 1fr 1fr;
			margin: unset;
		}

		@include xl {
			grid-template-columns: minmax(0, 1fr);
		}

		@include lg {
			width: auto;
			max-width: 250px;
			display: grid;
			grid-gap: 15px;
			grid-template-columns: minmax(0, 1fr);
			margin-right: unset;
			@include rem(margin, 15px 0);
		}
	}
}

.suggested-text {
	h1 {
		grid-column-start: 1;
		grid-column-end: 3;
		margin-top: 0;
		margin-bottom: 0;
		color: $subheader-color;
	}

	.section-description {
		@include rem(margin-top, 12px);
		grid-column-start: 1;
		grid-column-end: 3;
		font-size: $b360-font-size--base;
	}
}

.view-all,
/deep/ .view-all {
	//deep here bc ClientConnectDashboardList.vue uses same styling for view-all button
	width: 100%;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
	border-width: 0;
}

.description-link {
	@include typo--roboto__medium;
	text-decoration: none;
	color: $brand-blue;
	transition: color 0.25s ease;

	&:hover,
	&:focus {
		color: $b360-color--blue__darker;
	}
}

.social-composer-nav {
	border-bottom: $border-width solid $border-color-light;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	@include rem('padding', 0 20px);
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;

	button {
		& + * {
			@include rem('margin-left', 28px);
		}

		@include rem('padding', 0 0 19px);
		text-decoration: none;
		color: $text-base-color;
		position: relative;
		transition: $b360-motion-selection;
		border: none;
		background: none;
		font-size: inherit;
		cursor: pointer;

		&:after {
			content: '';
			position: absolute;
			width: 100%;
			height: 2px;
			left: 0;
			bottom: -1px;
			background: transparent;
			transition: $b360-motion-selection;
		}

		&:hover,
		&:focus,
		&.active {
			color: $brand-blue;

			&:after {
				background: $brand-blue;
			}
		}

		&:focus {
			outline: none;
		}
	}
}

.suggested-post {
	cursor: pointer;

	&.last-post {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}
}

.suggested-post-list {
	display: grid;
	grid-template-columns: 1fr;
	@include rem(grid-gap, 15px);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $b360-motion-duration--shortest
		$b360-motion-easing--standard;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}
</style>
